import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Grid, Box, Breadcrumbs, Typography } from '@mui/material';
import styles from './upload.module.scss';
import { UploadData } from 'components/UploadData';
import { UploadFile } from 'components/UploadFile';
import { GenericTable } from 'components/common/GenericTable';
import { useDispatch, useSelector } from 'react-redux';
import { FETCH_FILE_DATA, UPDATE_FILE } from '_redux/types/files.types';
import { SALES_COLUMNS } from 'utils/salesData';
import { filesActions } from '_redux/slices/files.slice';
import { Link, useParams } from 'react-router-dom';
import ModalContainer from 'components/common/ModalContainer/ModalContainer';
import { getAdvisorsList } from '_redux/services/advisor.service';

const Upload = () => {
  const dispatch = useDispatch();
  const { id } = useParams();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [step, setStep] = useState('uploadData');
  const [isModalOpen, setModalOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [data, setData] = useState([]);
  const [advisorsList, setAdvisorsList] = useState([]);

  const fileData = useSelector(filesActions.selectors.getFileData);

  const fetchAdvisorsList = useCallback(async () => {
    try {
      const advisorsData = await getAdvisorsList();
      setAdvisorsList(advisorsData);
    } catch (error) {
      console.error('Error fetching advisors:', error);
    }
  }, []);

  useEffect(() => {
    fetchAdvisorsList();
  }, [fetchAdvisorsList]);

  const handleUploadClick = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleFileUploadComplete = useCallback(() => {
    setModalOpen(false);
  }, []);

  useEffect(() => {
    if (id) {
      setStep('salesData');
      dispatch({ type: FETCH_FILE_DATA, payload: id });
    }
  }, [id, dispatch, refresh]);

  const handleUpdateRow = useCallback(
    async updatedData => {
      try {
        dispatch({
          type: UPDATE_FILE,
          payload: updatedData,
          id: id,
        });
        setEdit(false);
      } catch (error) {
        console.error('Error updating data', error);
      }
    },
    [dispatch, id]
  );

  const handleEditBtnClick = useCallback(() => {
    setEdit(true);
  }, []);

  const handleCancelClick = useCallback(() => {
    setEdit(false);
  }, []);

  useEffect(() => {
    setData(fileData);
  }, [fileData]);

  // Use memoization for paginated data to avoid recalculating on each render
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return data?.SalesData?.slice(startIndex, endIndex) || [];
  }, [data, page, rowsPerPage]);

  const toggleRefresh = () => {
    setRefresh(prev => !prev);
  };

  const handleAdvisorChange = (row, value) => {
    const updatedData = [
      {
        updatedFields: {
          advisorName: value,
        },
        id: row.id,
      },
    ];
    dispatch({
      type: UPDATE_FILE,
      payload: updatedData,
      id: id,
    });
  };

  const columns = useMemo(
    () => SALES_COLUMNS(advisorsList, handleAdvisorChange),
    [advisorsList, handleAdvisorChange]
  );

  const renderContent = useMemo(() => {
    switch (step) {
      case 'uploadData':
        return <UploadData onUploadClick={handleUploadClick} />;
      case 'uploadFile':
        return (
          <UploadFile
            onUploadComplete={handleFileUploadComplete}
            limit={rowsPerPage}
            skip={page * rowsPerPage}
          />
        );
      case 'salesData':
        return (
          <GenericTable
            title="Sales Data"
            data={paginatedData}
            columns={columns}
            pageConfig={{
              page,
              setPage,
              rowsPerPage,
              setRowsPerPage,
              totalRows: data?.SalesData?.length || 0,
            }}
            isDashboard={true}
            isEdit={edit}
            isDownload={false}
            onUpdateRows={handleUpdateRow}
            handleEditClick={handleEditBtnClick}
            onCancelClick={handleCancelClick}
            isEditable={true}
            refreshData={toggleRefresh}
            isUploadFiles={true}
            selectedFileId={id}
            setData={setData}
          />
        );
      default:
        return null;
    }
  }, [
    step,
    fileData,
    paginatedData,
    handleUploadClick,
    handleFileUploadComplete,
    handleUpdateRow,
    handleEditBtnClick,
    handleCancelClick,
    rowsPerPage,
    page,
    edit,
  ]);

  return (
    <Box className={styles.dashboardContainer}>
      {step !== 'uploadData' && (
        <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
          <Link
            to="/upload"
            underline="hover"
            color="#666666"
            onClick={() => setStep('uploadData')}
          >
            Upload Data
          </Link>
          {step === 'salesData' && (
            <Typography color="textPrimary">Sales Data</Typography>
          )}
        </Breadcrumbs>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {renderContent}
        </Grid>
      </Grid>
      <ModalContainer open={isModalOpen}>
        <UploadFile
          onUploadComplete={handleFileUploadComplete}
          limit={rowsPerPage}
          skip={page * rowsPerPage}
        />
      </ModalContainer>
    </Box>
  );
};

export default Upload;
